import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { login } from '../api/auth'

function HexMark({ size = 30 }) {
  return (
    <svg
      className="tg-mark"
      viewBox="0 0 32 32" width={size} height={size} fill="none"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="hexL">
          <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="32" height="18.6" fill="currentColor" opacity="0.18" clipPath="url(#hexL)" />
      <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <line x1="6" y1="18.6" x2="26" y2="18.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

export default function Login({ onLogin }) {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  function pickLang(code) {
    localStorage.setItem('tg_lang', code)
    window.dispatchEvent(new Event('tg_lang_change'))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await login(email, password)
      onLogin()
    } catch {
      setError('Invalid email or password')
    }
    setLoading(false)
  }

  return (
    <div className="auth-wrap">
      {/* LEFT: brand panel */}
      <section className="brand-panel">
        <div className="bp-grid" />

        <div className="bp-brand">
          <HexMark size={30} />
          <span className="wm">Tankguard</span>
        </div>

        <div className="bp-mid">
          <h1>Every liter, every truck, every minute.</h1>
          <p>Sign in to the control room for your fleet's fuel. Live levels, theft alerts, and the full audit trail — one secure place.</p>

          <div className="mini-card">
            <div className="mc-head">
              <span className="mc-nm">Truck-04</span>
              <span className="tg-badge tg-badge--sm tg-badge--online">
                <span className="tg-badge__dot" />Live
              </span>
            </div>
            <div className="mc-lbl">Fuel on board</div>
            <div className="mc-val">612 <span className="u">L</span></div>
            <div className="mc-bar"><span /></div>
          </div>
        </div>

        <div className="bp-foot">
          <span>© 2026 Tankguard GmbH</span>
          <a href="mailto:hello@tankguard.de?subject=Privacy%20policy%20request">Privacy</a>
          <a href="mailto:hello@tankguard.de?subject=Imprint%20request">Imprint</a>
          <span>Data hosted in Germany</span>
        </div>
      </section>

      {/* RIGHT: form panel */}
      <section className="form-panel">
        <div className="fp-top">
          <div className="lang-switch" role="group" aria-label="Language">
            <button type="button" aria-pressed={i18n.language === 'en'} onClick={() => pickLang('en')}>EN</button>
            <button type="button" aria-pressed={i18n.language === 'de'} onClick={() => pickLang('de')}>DE</button>
          </div>
        </div>

        <div className="fp-body">
          <div className="fp-inner">
            <h2>{t('auth.signIn')}</h2>
            <p className="sub">Welcome back. Enter your credentials to continue.</p>

            <form className="form" onSubmit={handleSubmit}>
              <div className="tg-field">
                <label className="tg-label" htmlFor="login-email">{t('auth.email')}</label>
                <input
                  id="login-email" className="tg-input" type="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" autoComplete="email" required autoFocus
                />
              </div>

              <div className="tg-field">
                <div className="row-between">
                  <label className="tg-label" htmlFor="login-pw">{t('auth.password')}</label>
                  <a className="link" href="/forgot-password">{t('auth.forgotPassword')}</a>
                </div>
                <input
                  id="login-pw" className="tg-input" type="password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••" autoComplete="current-password" required
                />
              </div>

              <div className="check-row">
                <input
                  type="checkbox" id="login-remember" checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                />
                <label htmlFor="login-remember">Keep me signed in on this device</label>
              </div>

              {error && <p className="tg-error">{error}</p>}

              <button
                type="submit"
                className={`tg-btn tg-btn--lg tg-btn--primary btn-full${loading ? ' is-loading' : ''}`}
                disabled={loading}
              >
                <span className="tg-spinner" aria-hidden="true" />
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </form>

            <div className="switch-line">
              No account yet? <a href="mailto:hello@tankguard.de?subject=Tankguard%20demo%20request">Request a demo</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
